import { useFragment } from '@apollo/client';
import { plural, t } from '@lingui/core/macro';
import { TeamAvatar, UserAvatar } from '@pocketlaw/tetris';
import { SearchableMenu, SearchableMenuMultiSelectList } from 'shared/domains/common-ui/SearchableMenu';
import { TeamsAndUsersMenuFragmentDoc } from './TeamsAndUsersMenu.gql';
export function TeamsAndUsersMenu(props) {
    const { type, from, renderTarget, selectedIds, onSelect } = props;
    const { complete, data } = useFragment({
        from,
        fragment: TeamsAndUsersMenuFragmentDoc,
        fragmentName: 'TeamsAndUsersMenu',
    });
    if (!complete) {
        return null;
    }
    const teams = (data === null || data === void 0 ? void 0 : data.teams) || [];
    const users = (data === null || data === void 0 ? void 0 : data.companyUsers) || [];
    const isValidUser = (user) => { var _a; return Boolean((_a = user.user) === null || _a === void 0 ? void 0 : _a.id); };
    const userNameFallback = t({
        message: 'Deleted user',
        comment: 'Fallback name when user does not exist',
    });
    const getMembersSubtitle = (members = 0) => plural(members, {
        0: 'No members',
        one: '# member',
        other: '# members',
    });
    const teamGroupLabel = t({
        message: 'Teams',
        comment: 'Teams and users menu - Teams group label',
    });
    const userGroupLabel = t({
        message: 'Users',
        comment: 'Teams and users menu - Users group label',
    });
    const normalizedTeams = teams.map((team) => {
        var _a;
        return ({
            type: 'team',
            id: team.id,
            title: team.name,
            subtitle: getMembersSubtitle((_a = team.members) === null || _a === void 0 ? void 0 : _a.length),
            data: {
                id: team.id,
                name: team.name,
            },
        });
    });
    const normalizedUsers = users.filter(isValidUser).map((user) => {
        var _a, _b, _c, _d, _e, _f, _g;
        return ({
            type: 'user',
            id: user.user.id,
            title: (_a = user.user.name) !== null && _a !== void 0 ? _a : userNameFallback,
            subtitle: (_c = (_b = user.user) === null || _b === void 0 ? void 0 : _b.email) !== null && _c !== void 0 ? _c : '',
            data: {
                companyUserId: user.id,
                userId: user.user.id,
                name: (_e = (_d = user.user) === null || _d === void 0 ? void 0 : _d.name) !== null && _e !== void 0 ? _e : userNameFallback,
                email: (_g = (_f = user.user) === null || _f === void 0 ? void 0 : _f.email) !== null && _g !== void 0 ? _g : '',
            },
        });
    });
    const placeholderMap = {
        all: t({
            message: 'Find users & teams...',
            comment: 'Teams and users menu - Search for teams and users',
        }),
        teams: t({
            message: 'Find team...',
            comment: 'Teams and users menu - Search for teams',
        }),
        users: t({
            message: 'Find user...',
            comment: 'Teams and users menu - Search for users',
        }),
    };
    const itemsMap = {
        all: [...normalizedTeams, ...normalizedUsers],
        teams: normalizedTeams,
        users: normalizedUsers,
    };
    const items = itemsMap[type];
    const placeholder = placeholderMap[type];
    const handleChange = (item) => {
        if (item.type === 'team') {
            onSelect({ type: 'team', team: item.data });
        }
        else {
            onSelect({ type: 'user', user: item.data });
        }
    };
    return (<SearchableMenu items={items} keys={['title']} placeholder={placeholder} renderTarget={renderTarget}>
      <SearchableMenuMultiSelectList selected={selectedIds} onSelect={handleChange} groupBy={{
            key: 'type',
            getLabel: (key) => (key === 'team' ? teamGroupLabel : userGroupLabel),
        }} renderItem={(item) => item.type === 'team' ? (<TeamAvatar size="sm" key={item.id} title={item.title} subtitle={item.subtitle}/>) : (<UserAvatar size="sm" key={item.id} title={item.title} subtitle={item.subtitle}/>)}/>
    </SearchableMenu>);
}
